import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Upload } from "lucide-react";

const STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal", label: "Proposal" },
  { key: "closed_won", label: "Won", color: "text-gain" },
  { key: "closed_lost", label: "Lost", color: "text-live" },
];

const BLANK = { name: "", phone: "", email: "", source: "", stage: "new", notes: "", estimated_value: 0, cost: 0 };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [csvText, setCsvText] = useState("");

  const load = async () => { const { data } = await api.get("/leads"); setLeads(data); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(BLANK); setShowModal(true); };
  const openEdit = (lead) => { setEditing(lead); setForm({ ...lead }); setShowModal(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0, cost: Number(form.cost) || 0 };
      if (editing) { await api.patch(`/leads/${editing.id}`, payload); toast.success("Updated"); }
      else { await api.post("/leads", payload); toast.success("Lead added"); }
      setShowModal(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const changeStage = async (lead, stage) => {
    try { await api.patch(`/leads/${lead.id}`, { stage }); load(); } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (k) => headers.findIndex((h) => h === k || h.includes(k));
    const nameI = idx("name"), phoneI = idx("phone"), emailI = idx("email"), sourceI = idx("source"), costI = idx("cost"), valI = idx("value");
    return lines.slice(1).map((row) => {
      const cols = row.split(",").map((c) => c.trim());
      return {
        name: cols[nameI] || "Unknown",
        phone: phoneI >= 0 ? cols[phoneI] || "" : "",
        email: emailI >= 0 ? cols[emailI] || "" : "",
        source: sourceI >= 0 ? cols[sourceI] || "" : "",
        cost: costI >= 0 ? Number(cols[costI]) || 0 : 0,
        estimated_value: valI >= 0 ? Number(cols[valI]) || 0 : 0,
        stage: "new", notes: "",
      };
    });
  };

  const importCSV = async (e) => {
    e.preventDefault();
    const parsed = parseCSV(csvText);
    if (!parsed.length) return toast.error("No rows parsed");
    try {
      const { data } = await api.post("/leads/bulk", parsed);
      toast.success(`${data.inserted} leads imported`);
      setShowCSV(false); setCsvText(""); load();
    } catch { toast.error("Import failed"); }
  };

  const byStage = (key) => leads.filter((l) => l.stage === key);

  return (
    <Shell>
      <div className="p-4 sm:p-8" data-testid="leads-page">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="label-xs text-amber">&gt; PIPELINE</div>
            <h1 className="font-display text-3xl sm:text-5xl leading-none mt-2">Leads.</h1>
            <p className="mt-2 text-fg-muted text-xs font-mono uppercase tracking-widest">{leads.length} TOTAL</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowCSV(true)} className="btn-ghost" data-testid="csv-import-btn"><Upload size={14} /> CSV</button>
            <button onClick={openCreate} className="btn-primary" data-testid="add-lead-btn"><Plus size={14} /> New Lead</button>
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-6" data-testid="kanban-board">
          {STAGES.map((s) => (
            <div key={s.key} className="min-w-[260px] flex-shrink-0 bg-raised border border-term" data-testid={`column-${s.key}`}>
              <div className="px-3 py-2.5 border-b border-term flex items-center justify-between">
                <span className={`label-xs ${s.color || "text-amber"}`}>{s.label}</span>
                <span className="font-mono text-[10px] text-fg-subtle">[{byStage(s.key).length}]</span>
              </div>
              <div className="p-2.5 space-y-2 min-h-[200px]">
                {byStage(s.key).map((l) => (
                  <div key={l.id} className="tap-card bg-subtle p-3" data-testid={`lead-card-${l.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <button onClick={() => openEdit(l)} className="font-bold text-left flex-1 text-sm text-fg">{l.name}</button>
                      <button onClick={() => del(l.id)} className="text-fg-subtle hover:text-live" data-testid={`delete-lead-${l.id}`}><Trash2 size={12} /></button>
                    </div>
                    {l.phone && <div className="text-[11px] text-fg-muted font-mono mt-1">{l.phone}</div>}
                    {l.source && <div className="text-[10px] text-fg-subtle mt-1 uppercase tracking-widest">SRC: {l.source}</div>}
                    {(l.estimated_value > 0) && <div className="text-[11px] font-mono text-gain mt-2">${l.estimated_value}</div>}
                    <select
                      value={l.stage}
                      onChange={(e) => changeStage(l, e.target.value)}
                      className="w-full mt-2 text-[10px] border border-term px-2 py-1 font-mono bg-black text-fg uppercase tracking-widest"
                      data-testid={`stage-select-${l.id}`}
                    >
                      {STAGES.map((s2) => <option key={s2.key} value={s2.key}>{s2.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-raised border border-term p-6 max-w-lg w-full" data-testid="lead-modal">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <div className="label-xs text-amber">&gt; {editing ? "EDIT" : "NEW"}</div>
                  <div className="font-display text-2xl mt-1">Lead Record</div>
                </div>
                <button type="button" onClick={() => setShowModal(false)} className="text-fg-muted"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <input required placeholder="Name" className="input-term" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" />
                <input placeholder="Phone" className="input-term" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" />
                <input placeholder="Email" className="input-term" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input placeholder="Source (Facebook, Referral, etc)" className="input-term" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
                <select className="input-term" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
                  {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" placeholder="Est. Value $" className="input-term" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} />
                  <input type="number" placeholder="Lead Cost $" className="input-term" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                </div>
                <textarea placeholder="Notes" rows={3} className="input-term" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <button type="submit" className="btn-primary mt-5 w-full" data-testid="lead-save-btn">{editing ? "Save >" : "Add Lead >"}</button>
            </form>
          </div>
        )}

        {showCSV && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCSV(false)}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={importCSV} className="bg-raised border border-term p-6 max-w-2xl w-full" data-testid="csv-modal">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="label-xs text-amber">&gt; BULK IMPORT</div>
                  <div className="font-display text-2xl mt-1">CSV Import</div>
                </div>
                <button type="button" onClick={() => setShowCSV(false)} className="text-fg-muted"><X size={18} /></button>
              </div>
              <div className="label-xs mb-2">EXPECTED HEADERS: name, phone, email, source, cost, value</div>
              <textarea
                placeholder={"name,phone,email,source,cost,value\nJohn Doe,+15551234567,john@x.com,Facebook,15,500"}
                rows={10}
                className="input-term font-mono text-xs"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                data-testid="csv-text-input"
              />
              <button type="submit" className="btn-primary mt-4 w-full" data-testid="csv-import-submit">Import &gt;</button>
            </form>
          </div>
        )}
      </div>
    </Shell>
  );
}
